<script lang="ts">import { onDestroy } from "svelte";
import { AppMode, cfgAppMode, cfgEventThemeCdnPath, cfgToThemeCdnPath, FeatureSet, isEventAssetEnabled, RELEASE, ReleaseStage, Stage, stateFeatureSet, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isFullScreenGame, isMobile, loggedIn, ModalDialog, selectedItemMobileMenu, stateOpenModal, toSiteUrl } from "@infinitro/global";
let lobbyHref = "/";
let logoPressTimer = null;
let isLogoLongPressed = false;
// Reactive statements for event logo
$: useEventLogo = isEventAssetEnabled("customLogo");
$: useAlternative = $stateUi.header.headerStyle === "alternative";
$: useAlternative2 = $stateUi.header.headerStyle === "alternative2";
$: useAlternative4 = $stateUi.header.headerStyle === "alternative4";
$: useAlternative2PublicLogo = useAlternative2 && !$loggedIn;
$: useDesktopLogoOnMobileByBrand = $isMobile && $stateUi.games.showLogoWhenPlayingDesktop;
$: useDesktopLogoOnMobileGame = $isMobile && $isFullScreenGame && $loggedIn && $stateUi.games.showNavigationOnMobile;
$: useDesktopLogoOnMobile = useDesktopLogoOnMobileByBrand || useDesktopLogoOnMobileGame;
$: desktopLogoPath = useEventLogo ? cfgEventThemeCdnPath("customlogo.png") : cfgToThemeCdnPath("/misc/logo.svg");
$: mobileLogoPath = useDesktopLogoOnMobileGame && $loggedIn ? desktopLogoPath : useEventLogo ? cfgEventThemeCdnPath("customlogomobile.png") : useAlternative2 ? cfgToThemeCdnPath("/misc/logo.svg") : cfgToThemeCdnPath("/misc/logo-mobile.svg");
const startPressLogo = (event) => {
	logoPressTimer = setTimeout(() => {
		isLogoLongPressed = true;
		console.log("logo was long-pressed");
		stateOpenModal.set(ModalDialog.DevSettings);
	}, RELEASE === Stage.Prod ? 7e3 : 3e3);
};
stateOpenModal.subscribe((value) => {
	isLogoLongPressed = value === ModalDialog.DevSettings;
});
const logoClick = (event) => {
	selectedItemMobileMenu.set(null);
	if (isLogoLongPressed) {
		event?.preventDefault();
		return false;
	}
};
const endPressLogo = (event) => {
	if (logoPressTimer) {
		clearTimeout(logoPressTimer);
		logoPressTimer = null;
	}
	if (isLogoLongPressed) {
		event?.preventDefault();
		return false;
	}
	isLogoLongPressed = false;
};
onDestroy(() => {
	if (logoPressTimer) {
		clearTimeout(logoPressTimer);
		logoPressTimer = null;
	}
});
$: {
	lobbyHref = toSiteUrl("/", $loggedIn);
}
</script>

<div class="logo-container">
	<a
		href={lobbyHref}
		on:mousedown={startPressLogo}
		on:mouseup={endPressLogo}
		on:mouseleave={endPressLogo}
		on:touchstart={startPressLogo}
		on:touchend={endPressLogo}
		on:touchcancel={endPressLogo}
		on:click={logoClick}
		class="logo-link"
	>
		<!-- Desktop Logo -->
		<div
			class="logo-desktop"
			class:logo-desktop-alternative={useAlternative || useAlternative4}
			class:logo-desktop-alternative2={useAlternative2}
		>
			<img src={desktopLogoPath} alt={$t(Verbiage.site_logo_alt, 'alts')} />
		</div>

		<!-- Mobile Logo -->
		<div
			class="logo-mobile"
			class:logo-mobile-alternative={useAlternative || useAlternative4}
			class:logo-mobile-alternative2={useAlternative2}
			class:logo-mobile-alternative4={useAlternative4}
			class:logo-mobile-desktop={useDesktopLogoOnMobileByBrand}
			class:logo-mobile-game={useDesktopLogoOnMobileGame}
		>
			<img src={mobileLogoPath} alt={$t(Verbiage.site_logo_alt, 'alts')} />
		</div>
	</a>
</div>

<style>
	.logo-container {

		display: flex;

		height: 4rem;

		width: 100%;

		align-items: center;

		justify-content: flex-start;

		padding-top: 0px;

		padding-bottom: 0px;

		padding-left: 0px;

		padding-right: 0px
}

	.logo-link {

		display: flex;

		align-items: center
}

	/* Desktop logo styling */
	.logo-desktop {

		display: flex;

		height: 40px;

		--tw-text-opacity: 1;

		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.logo-desktop img {

		height: 100%;

		max-height: 40px;

		width: auto;

		max-width: 125px;

		-o-object-fit: contain;

		   object-fit: contain
}

	/* Mobile logo styling */
	.logo-mobile {

		display: none;

		--tw-text-opacity: 1;

		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.logo-mobile img {

		margin-right: 1rem;

		height: auto;

		max-height: 40px;

		width: auto;

		max-width: 40px;

		-o-object-fit: contain;

		   object-fit: contain
}

	.logo-mobile-alternative img {

		height: 55px;

		width: 55px;

		max-height: 55px;

		max-width: 55px
}

	.logo-mobile-alternative2 img {

		height: auto;

		width: 100%;

		max-height: 40px;

		max-width: 95%
}

	.logo-mobile-alternative4 img {

		height: auto;

		width: 80px;

		max-height: unset;

		max-width: 80px
}

	/* Responsive breakpoints */
	@media (min-width: 641px) {
		.logo-desktop {

				display: flex
		}

		.logo-mobile {

				display: none
		}
	}

	@media (max-width: 640px) {
		.logo-desktop {

				display: none
		}

		.logo-mobile {

				display: flex
		}
	}

	@media (min-width: 1024px) {
		.logo-desktop-alternative2 img {

				max-width: 182px
		}
	}

	.logo-mobile-game img {

		margin-right: 1rem;

		height: 100%;

		max-height: 40px;

		width: auto;

		max-width: 125px;

		-o-object-fit: contain;

		   object-fit: contain
}

	.logo-mobile-desktop img {

		margin-right: 1rem;

		height: 100%;

		max-height: 36px;

		width: auto;

		max-width: 95px;

		-o-object-fit: contain;

		   object-fit: contain
}
</style>
